import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';

import { Controller, ErrorOption, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { Control, FieldErrors } from 'react-hook-form/dist/types';
import { MultiValue, SingleValue } from 'react-select';
import { CSSTransition } from 'react-transition-group';
import { Link } from 'react-router-dom';
import debounce from 'debounce-promise';

import { PreQualificationStep, setPreQualificationStep } from 'actions';
import { setData } from 'actions/data';
import { setPrequalificationPortal } from 'actions/prequalificationPortal';
import { IntermediarySuggestionDto } from 'api/IntermediariesApi';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import Input from 'components/Inputs/Input';
import SelectCreatable from 'components/SelectCreatable';
import { ContractorInformationLinkButtonsLabel } from 'enums/ContractorInformationLinkButtonsLabel';
import { ContractorInputName } from 'enums/ContractorInputName';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { IntermediaryApiContext } from 'index';
import { AppRoutes } from 'routes/routes.enums';
import { getInputSelectOptions, OptionType } from 'utils/inputSelect';
import { getMessageForRequiredFields } from 'utils/validationUtils';
import { ContractorNameSource } from 'enums/ApplicationsVariables';
import { Text } from 'pages/Dashboard/steps/AcceptOffer/acceptOffer.enums';
import { MIN_LOAN_FOR_PORTAL } from 'pages/PreQualification/steps/LoanAmount/loanAmount.constants';
import { DEFAULT_DEBOUNCE_TIMEOUT } from 'utils/constants';

import styles from './ContractorForm.module.scss';

export interface FormDataContractorInfo {
  [ContractorInputName.ContractorName]: string;
  [ContractorInputName.SalespersonName]: string;
  [ContractorInputName.AgreeToCheckbox]: boolean;
  [ContractorInputName.ContractorAutofillUsed]: ContractorNameSource | null;
}

export interface ContractorInputLabelsType {
  contractorName: string;
  salespersonName: string;
  contractorNameRestrict?: string;
  contractorAutofillUsed?: ContractorNameSource | null;
}

export interface ContractorFormProps {
  watcher: Record<string, any>;
  reset: (contractorValues?: ContractorInputLabelsType) => void;
  setError: (
    name: ContractorInputName.ContractorName | ContractorInputName.SalespersonName,
    error: ErrorOption,
  ) => void;
  setValue: UseFormSetValue<FormDataContractorInfo>;
  trigger: UseFormTrigger<FormDataContractorInfo>;
  errors?: FieldErrors<FormDataContractorInfo>;
  loading?: boolean | null;
  control?: Control<FormDataContractorInfo>;
  labels?: ContractorInputLabelsType;
  required?: boolean;
  clearErrors?: (name?: ContractorInputName | ContractorInputName[]) => void;
  errorReferenceNames?: ContractorInputLabelsType;
  isDisabled?: boolean;
}

const buildSalespersonsObjects = (selectedOption: IntermediarySuggestionDto | null) => {
  if (!selectedOption?.variables?.intermediary_salespersons) {
    return {};
  }

  return selectedOption.variables.intermediary_salespersons.reduce((accumulator, variable) => {
    if (!variable.name) {
      return accumulator;
    }

    return { ...accumulator, [variable.name]: { phone: variable.phone, email: variable.email } };
  }, {});
};

const ContractorForm = ({
  errors,
  setError,
  setValue,
  trigger,
  loading,
  control,
  watcher,
  reset,
  labels,
  clearErrors,
  errorReferenceNames,
  required = false,
  isDisabled,
}: ContractorFormProps) => {
  const dispatch = useAppDispatch();
  const { borrower, coBorrower, application } = useAppSelector((state) => state);
  const isHilProduct =
    application?.[LoanInformationVariables.LoanAmount] &&
    (application?.[LoanInformationVariables.LoanAmount] as number) > MIN_LOAN_FOR_PORTAL;

  const { salespersonData } = useAppSelector((state) => state.data);
  const [foundSuggestions, setSuggestions] = useState<IntermediarySuggestionDto[]>([]);
  const [currentContractorNameValue, setCurrentContractorNameValue] = useState<string>('');
  const [currentSalespersonNameValue, setCurrentSalespersonNameValue] = useState<string>('');

  const intermediaryApi = useContext(IntermediaryApiContext);

  const fetchSuggestions = async (input: string): Promise<IntermediarySuggestionDto[]> => {
    const contractors = await intermediaryApi.getSuggestions({ name: input });

    setSuggestions(contractors);

    return contractors;
  };

  const setSalesPersons = (selectedOption: IntermediarySuggestionDto) => {
    const salespersonsObjects = buildSalespersonsObjects(selectedOption);

    dispatch(setData({ salespersonData: salespersonsObjects }));
  };

  useEffect(() => {
    dispatch(setPreQualificationStep(PreQualificationStep.Contractor));

    //TODO need to think about it. Component doesn't need to do it. It makes code difficult to analyze. it is the responsibility of redux and redux persist
    const dataToSaveInLocalStorage = {
      borrower: {
        firstName: borrower.firstName,
        lastName: borrower.lastName,
        streetNumber: borrower.streetNumber,
        streetName: borrower.streetName,
        city: borrower.city,
        zip: borrower.zip,
        state: borrower.state,
      },
      coBorrower: {
        firstName: coBorrower.firstName,
        lastName: coBorrower.lastName,
        borrowerId: coBorrower.borrowerId,
      },
      application: {
        [LoanInformationVariables.LoanType]: application[LoanInformationVariables.LoanType],
        [LoanInformationVariables.LoanAmount]: application[LoanInformationVariables.LoanAmount],
      },
    };

    localStorage.setItem('sessionStorage', JSON.stringify(dataToSaveInLocalStorage));

    if (watcher[ContractorInputName.ContractorName]) {
      (async () => {
        const suggestions = await fetchSuggestions(watcher[ContractorInputName.ContractorName]);

        setSalesPersons(suggestions[0]);
      })();
    }

    return () => {
      localStorage.removeItem('sessionStorage');
    };
  }, []);

  useEffect(() => {
    dispatch(
      setData({
        contractorName: watcher[ContractorInputName.ContractorName],
        salesPerson: watcher[ContractorInputName.SalespersonName],
      }),
    );
    dispatch(
      setPrequalificationPortal({
        isContractorInfoCheckboxChecked: watcher[ContractorInputName.AgreeToCheckbox],
      }),
    );
  }, [watcher]);

  const loadContractorSuggestions = useCallback(
    debounce(async (input: string) => {
      try {
        if (input.length >= 4) {
          const suggestions = await fetchSuggestions(input);

          return getInputSelectOptions(suggestions.map((suggestion: any) => suggestion.variables.intermediary_name));
        } else {
          setSuggestions([]);

          setError(ContractorInputName.ContractorName, {
            type: 'manual',
            message: 'Please Enter at Least 4 Characters',
          });
        }

        return [];
      } catch (error) {
        setSuggestions([]);

        return [];
      }
    }, DEFAULT_DEBOUNCE_TIMEOUT),
    [],
  );

  const label =
    (errors && errors[ContractorInputName.ContractorName]) ||
    watcher[ContractorInputName.ContractorName] ||
    currentContractorNameValue
      ? labels?.contractorName
      : labels?.contractorNameRestrict;

  const formatCreateLabel = (input: string) => {
    return (
      <div className={styles.createLabelContainer}>
        I cannot find my contractor in the list. Add:
        <span className={styles.newOptionLabel}>{input}</span>
      </div>
    );
  };

  const handleContractorNameChange = (option: MultiValue<OptionType> | SingleValue<OptionType>) => {
    dispatch(
      setData({
        contractorName: '',
        salesPerson: '',
        salespersonData: {},
        contractorAutofillUsed: ContractorNameSource.Empty,
      }),
    );

    if (!option) {
      return reset();
    }

    if (clearErrors) {
      clearErrors([ContractorInputName.ContractorName]);
    }

    const name = (option as OptionType).__isNew__ ? (option as OptionType).value : (option as OptionType).label;
    const contractorAutofillUsed = (option as OptionType).__isNew__
      ? ContractorNameSource.Manual
      : ContractorNameSource.Autofill;

    reset({ contractorName: name, salespersonName: '', contractorAutofillUsed: contractorAutofillUsed });

    const selectedOption = foundSuggestions.find((suggestion) => suggestion.variables.intermediary_name === name);

    if (selectedOption) {
      setSalesPersons(selectedOption);
    }
  };

  const handleContractorNameInputChange = (newInputValue: string) => {
    setCurrentContractorNameValue(newInputValue);

    trigger('contractor_name');
  };

  const handleSalespersonNameInputChange = (newInputValue: string | ChangeEvent<HTMLInputElement>) => {
    if (typeof newInputValue === 'string') {
      setCurrentSalespersonNameValue(newInputValue);
    } else {
      reset({
        contractorName: watcher[ContractorInputName.ContractorName],
        salespersonName: newInputValue.target.value,
        contractorAutofillUsed: watcher[ContractorInputName.ContractorAutofillUsed],
      });
    }
  };

  const handleSalespersonSelectChange = (option: MultiValue<OptionType> | SingleValue<OptionType>) => {
    if (!option) {
      return reset({
        contractorName: watcher[ContractorInputName.ContractorName],
        salespersonName: '',
        contractorAutofillUsed: watcher[ContractorInputName.ContractorAutofillUsed],
      });
    }

    const salesperson = (option as OptionType).__isNew__ ? (option as OptionType).value : (option as OptionType).label;

    reset({
      contractorName: watcher[ContractorInputName.ContractorName],
      salespersonName: salesperson,
      contractorAutofillUsed: watcher[ContractorInputName.ContractorAutofillUsed],
    });
  };

  const getButtonLabelByRoute = (route: AppRoutes) => {
    switch (route) {
      case AppRoutes.AcceptTermsOfUse:
        return ContractorInformationLinkButtonsLabel.TermsOfUse;
      case AppRoutes.AcknowledgementAndConsent:
        return ContractorInformationLinkButtonsLabel.AcknowledgementAndConsent;
      case AppRoutes.Disclosure:
        return ContractorInformationLinkButtonsLabel.ESignConsentDisclosure;
    }
  };

  const getButtonLink = (route: AppRoutes) => (
    <a href={route} className={styles.checkboxTextAsLink}>
      {getButtonLabelByRoute(route)}
    </a>
  );

  return (
    <div className={styles.contractorFormContainer}>
      <div className={styles.formRow}>
        <SelectCreatable
          name={ContractorInputName.ContractorName}
          isValidNewOption={() => currentContractorNameValue.length > 3}
          useFormControl
          control={control}
          required={required}
          loadOptions={loadContractorSuggestions}
          label={label}
          formatCreateLabel={formatCreateLabel}
          onChange={handleContractorNameChange}
          onInputChange={handleContractorNameInputChange}
          value={getInputSelectOptions([watcher[ContractorInputName.ContractorName]])[0]}
          inputValue={currentContractorNameValue}
          isClearable={!!watcher[ContractorInputName.ContractorName]}
          loading={loading}
          inputContainerClassname={styles.input}
          errorMessage={errors && errors[ContractorInputName.ContractorName]?.message}
          errorMessageReferenceName={errorReferenceNames?.contractorNameRestrict}
          isDisabled={isDisabled}
        />
      </div>
      <CSSTransition
        in={watcher[ContractorInputName.ContractorName] !== ''}
        timeout={400}
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
        unmountOnExit
      >
        <div>
          <div className={styles.formRow}>
            {Object.keys(salespersonData).length === 0 ? (
              <Input
                value={watcher[ContractorInputName.SalespersonName]}
                onChange={handleSalespersonNameInputChange}
                name={ContractorInputName.SalespersonName}
                label={labels?.salespersonName}
                inputContainerClassName={styles.input}
                errorMessage={errors && errors[ContractorInputName.SalespersonName]?.message}
              />
            ) : (
              <SelectCreatable
                name={ContractorInputName.SalespersonName}
                useFormControl
                control={control}
                required={required}
                label={labels?.salespersonName}
                formatCreateLabel={formatCreateLabel}
                onChange={handleSalespersonSelectChange}
                onInputChange={handleSalespersonNameInputChange}
                value={
                  watcher[ContractorInputName.SalespersonName]
                    ? getInputSelectOptions([watcher[ContractorInputName.SalespersonName]])[0]
                    : { value: '', label: '' }
                }
                inputValue={currentSalespersonNameValue}
                options={getInputSelectOptions(Object.keys(salespersonData))}
                isClearable={!!watcher[ContractorInputName.SalespersonName]}
                inputContainerClassname={styles.input}
                errorMessage={errors && errors[ContractorInputName.SalespersonName]?.message}
                errorMessageReferenceName={errorReferenceNames?.salespersonName}
                loading={loading}
              />
            )}
          </div>
        </div>
      </CSSTransition>
      <Controller
        name={ContractorInputName.AgreeToCheckbox}
        control={control}
        rules={{
          required: getMessageForRequiredFields(),
          validate: (value) => value === true || getMessageForRequiredFields(),
        }}
        render={() => (
          <div className={styles.checkboxContainer}>
            <div className={styles.checkboxWrapper}>
              <Checkbox
                type={CheckboxType.Alternative}
                name={ContractorInputName.AgreeToCheckbox}
                isChecked={!!watcher[ContractorInputName.AgreeToCheckbox]}
                onChange={() => {
                  setValue(ContractorInputName.AgreeToCheckbox, !watcher[ContractorInputName.AgreeToCheckbox]);
                  trigger(ContractorInputName.AgreeToCheckbox);
                }}
                className={styles.checkboxText}
                isValid={!errors?.[ContractorInputName.AgreeToCheckbox]?.message}
              />
              <p className={styles.checkboxTextContainer}>
                <span>{getButtonLink(AppRoutes.Disclosure)},&nbsp;</span>
                <span>{getButtonLink(AppRoutes.AcceptTermsOfUse)},&nbsp;</span>
                {isHilProduct && (
                  <span>
                    <Link to={AppRoutes.TranscriptAuthorization} className={styles.checkboxTextAsLink}>
                      {Text.TranscriptAuthorization}
                    </Link>
                    ,&nbsp;
                  </span>
                )}
                To Opt-In For Text Message Notifications
              </p>
            </div>
            <p className={styles.errorMessage}>{errors?.[ContractorInputName.AgreeToCheckbox]?.message}</p>
          </div>
        )}
      />
      <p className={styles.checkingLabel}>
        I/We understand that by checking the above boxes, I/We have read and agree to the above linked terms. I/We
        understand that by checking the above boxes I/We certify that all of the information provided in this inquiry is
        true and correct and confirm that I/We have read and agree to the above linked terms. I/We authorize the company
        to obtain credit information to process the request I/We have submitted and confirm identity. I/We confirm that
        all persons who are party to this inquiry participated in the submission of this inquiry.
      </p>
    </div>
  );
};

export default ContractorForm;
